"use client"

import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/shad"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/shad/tooltip"
import { Info } from "lucide-react"

import type {
  CcUserTeamConfig,
  EmailRecipientsConfig,
  EmailRecipientType,
  TeamMemberRecipientsConfig,
} from "@/components/automations/types"
import { TeamMemberRecipientsField } from "@/components/sequences/team-member-recipients-field"

import { CcUserTeamPicker } from "./cc-user-team-picker-field"

interface EmailRecipientsFieldProps {
  value: EmailRecipientsConfig | undefined
  onChange: (value: EmailRecipientsConfig) => void
  contactOnly?: boolean
  label?: string
  includePerson?: boolean
  ccUserTeamValue?: CcUserTeamConfig
  onCcUserTeamChange?: (value: CcUserTeamConfig) => void
  teamMemberRecipientsValue?: TeamMemberRecipientsConfig
  onTeamMemberRecipientsChange?: (value: TeamMemberRecipientsConfig) => void
}

const RECIPIENT_TYPE_OPTIONS: { value: EmailRecipientType; label: string }[] = [
  { value: "to", label: "To" },
  { value: "cc", label: "CC" },
  { value: "bcc", label: "BCC" },
  { value: "none", label: "None" },
]

const USER_TEAM_TYPE_OPTIONS: { value: "cc" | "bcc" | "none"; label: string }[] = [
  { value: "cc", label: "CC" },
  { value: "bcc", label: "BCC" },
  { value: "none", label: "None" },
]

const PERSON_TOOLTIP =
  "The specific person associated with the trigger (e.g., the person whose birthday it is). If no specific person is provided by the trigger, falls back to the head of household. One message is sent per unique recipient — no duplicates."

const RECIPIENT_ROLES: { key: keyof EmailRecipientsConfig; label: string; tooltip?: string }[] = [
  { key: "head_of_household", label: "Head of household" },
  { key: "spouse_or_partner", label: "Spouse or partner" },
  { key: "person", label: "Person", tooltip: PERSON_TOOLTIP },
]

const DEFAULT_RECIPIENTS: EmailRecipientsConfig = {
  head_of_household: "to",
  spouse_or_partner: "none",
}

const DEFAULT_CC_USER_TEAM: CcUserTeamConfig = {
  user_ids: [],
  team_ids: [],
  type: "none",
}

export function EmailRecipientsField({
  value,
  onChange,
  label = "Recipients",
  includePerson = true,
  contactOnly = false,
  ccUserTeamValue,
  onCcUserTeamChange,
  teamMemberRecipientsValue,
  onTeamMemberRecipientsChange,
}: EmailRecipientsFieldProps) {
  const currentValue: EmailRecipientsConfig = value ?? (contactOnly ? { person: "to" } : DEFAULT_RECIPIENTS)
  const recipientRoles: typeof RECIPIENT_ROLES = contactOnly
    ? [{ key: "person", label: "Contact" }]
    : RECIPIENT_ROLES.filter((role) => includePerson || role.key !== "person")
  const ccUserTeam = ccUserTeamValue ?? DEFAULT_CC_USER_TEAM
  const showUserTeamPicker = ccUserTeam.type !== "none" && onCcUserTeamChange

  const handleRoleChange = (role: keyof EmailRecipientsConfig, newType: EmailRecipientType) => {
    onChange({
      ...currentValue,
      [role]: newType,
    })
  }

  const handleUserTeamTypeChange = (newType: "cc" | "bcc" | "none") => {
    if (!onCcUserTeamChange) return
    if (newType === "none") {
      onCcUserTeamChange({ user_ids: [], team_ids: [], type: "none" })
    } else {
      onCcUserTeamChange({ ...ccUserTeam, type: newType })
    }
  }

  return (
    <div>
      <Label className="text-sm font-medium">{label}</Label>
      <div className="mt-2 space-y-2">
        {recipientRoles.map((role) => (
          <div key={role.key} className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground flex items-center gap-1.5 text-sm whitespace-nowrap">
              {role.label}
              {role.tooltip && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="text-muted-foreground size-3.5 shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p>{role.tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </span>
            <Select
              value={currentValue[role.key] ?? "none"}
              onValueChange={(newValue) => handleRoleChange(role.key, newValue as EmailRecipientType)}
            >
              <SelectTrigger size="sm" className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECIPIENT_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}

        {onCcUserTeamChange && (
          <>
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground text-sm whitespace-nowrap">Users or teams</span>
              <Select value={ccUserTeam.type} onValueChange={handleUserTeamTypeChange}>
                <SelectTrigger size="sm" className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_TEAM_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {showUserTeamPicker && (
              <CcUserTeamPicker
                userIds={ccUserTeam.user_ids}
                teamIds={ccUserTeam.team_ids}
                onUserIdsChange={(userIds) => onCcUserTeamChange({ ...ccUserTeam, user_ids: userIds })}
                onTeamIdsChange={(teamIds) => onCcUserTeamChange({ ...ccUserTeam, team_ids: teamIds })}
              />
            )}
          </>
        )}

        {onTeamMemberRecipientsChange && (
          <TeamMemberRecipientsField value={teamMemberRecipientsValue ?? {}} onChange={onTeamMemberRecipientsChange} />
        )}
      </div>
    </div>
  )
}
